import { Suspense, lazy } from 'react'
import { AppProvider, useApp } from './context/AppContext'
import BottomNav from './components/BottomNav'
import Toast from './components/Toast'
import ExitConfirmDialog from './components/ExitConfirmDialog'
import PrivacyConsentModal from './components/PrivacyConsentModal'
import OfflineBanner from './components/OfflineBanner'
import UpdateDialog from './components/UpdateDialog'
import PageTransition from './components/PageTransition'

// 常驻页面（底部Tab对应的5个页面，切换时不重新挂载）
import CartPage from './pages/CartPage'
import AIPage from './pages/AIPage'
import LearnPage from './pages/LearnPage'
import ProfilePage from './pages/ProfilePage'

// 非常驻页面懒加载 — 减少首屏 JS 体积
const DetailPage = lazy(() => import('./pages/DetailPage'))
const SearchPage = lazy(() => import('./pages/SearchPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const VideoPage = lazy(() => import('./pages/VideoPage'))
const KnowledgeDetailPage = lazy(() => import('./pages/KnowledgeDetailPage'))
const KnowledgePage = lazy(() => import('./pages/KnowledgePage'))
const IdentitySelectPage = lazy(() => import('./pages/IdentitySelectPage'))
const HomeworkListPage = lazy(() => import('./pages/HomeworkListPage'))
const HomeworkDetailPage = lazy(() => import('./pages/HomeworkDetailPage'))
const HomeworkCreatePage = lazy(() => import('./pages/HomeworkCreatePage'))
const MistakeBookPage = lazy(() => import('./pages/MistakeBookPage'))
const LearnTrackPage = lazy(() => import('./pages/LearnTrackPage'))
const TeacherDashboardPage = lazy(() => import('./pages/TeacherDashboardPage'))
const AIRecommendPage = lazy(() => import('./pages/AIRecommendPage'))
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'))
const TermsPage = lazy(() => import('./pages/TermsPage'))
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const DownloadPage = lazy(() => import('./pages/DownloadPage'))
const FavoritesPage = lazy(() => import('./pages/FavoritesPage'))
const NotesPage = lazy(() => import('./pages/NotesPage'))
const CertificatesPage = lazy(() => import('./pages/CertificatesPage'))
const InvitePage = lazy(() => import('./pages/InvitePage'))
const HelpPage = lazy(() => import('./pages/HelpPage'))
const WalletPage = lazy(() => import('./pages/WalletPage'))

// 隐藏底部导航的页面
const HIDE_NAV = [
  'detail', 'settings', 'search', 'login', 'video', 'identity',
  'homework', 'homeworkList', 'homeworkCreate', 'homeworkHistory',
  'mistakeBook', 'learnTrack', 'teacherDashboard', 'aiRecommend',
  'privacy', 'terms', 'dashboard', 'downloads',
  'favorites', 'certificates', 'notes', 'invite', 'help', 'wallet'
]

// 常驻内存的主页面
const PERSISTENT_PAGES = ['learn', 'courses', 'cart', 'ai', 'profile']

function AppInner() {
  const { page, darkMode } = useApp()
  const showNav = !HIDE_NAV.includes(page)

  const renderTransientPage = () => {
    if (PERSISTENT_PAGES.includes(page)) return null
    return (
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}>
        <PageTransition pageKey={page}>
          <Suspense>
            {page === 'detail'   && <DetailPage />}
            {page === 'search'   && <SearchPage />}
            {page === 'settings' && <SettingsPage />}
            {page === 'login'    && <LoginPage />}
            {page === 'video'    && <VideoPage />}
            {page === 'knowledge' && <KnowledgeDetailPage />}
            {page === 'identity' && <IdentitySelectPage />}
            {page === 'homeworkList' && <HomeworkListPage />}
            {page === 'homework'     && <HomeworkDetailPage />}
            {page === 'homeworkCreate' && <HomeworkCreatePage />}
            {page === 'homeworkHistory' && <HomeworkListPage />}
            {page === 'mistakeBook'   && <MistakeBookPage />}
            {page === 'learnTrack'    && <LearnTrackPage />}
            {page === 'teacherDashboard' && <TeacherDashboardPage />}
            {page === 'aiRecommend' && <AIRecommendPage />}
            {page === 'privacy'  && <PrivacyPage />}
            {page === 'terms'    && <TermsPage />}
            {page === 'dashboard' && <DashboardPage />}
            {page === 'downloads' && <DownloadPage />}
            {page === 'favorites' && <FavoritesPage />}
            {page === 'notes' && <NotesPage />}
            {page === 'certificates' && <CertificatesPage />}
            {page === 'invite' && <InvitePage />}
            {page === 'help' && <HelpPage />}
            {page === 'wallet' && <WalletPage />}
          </Suspense>
        </PageTransition>
      </div>
    )
  }

  return (
    <div className={`phone-shell${darkMode ? ' dark-mode' : ''}`} data-theme={darkMode ? 'dark' : 'light'}>
      <div className="phone-screen" style={{ background: darkMode ? '#1a1a2e' : '#F5F6FA', transition: 'background 0.3s' }}>
        <OfflineBanner />

        {PERSISTENT_PAGES.map(p => (
          <div
            key={p}
            style={{
              display: page === p ? 'flex' : 'none',
              flex: page === p ? 1 : undefined,
              flexDirection: 'column',
              overflowY: page === p ? 'auto' : 'hidden',
              overflowX: 'hidden',
              position: 'relative',
              height: page === p ? undefined : 0,
            }}
          >
            {p === 'learn'   && <LearnPage />}
            {p === 'courses' && (
              <Suspense>
                <KnowledgePage />
              </Suspense>
            )}
            {p === 'cart'    && <CartPage />}
            {p === 'ai'      && <AIPage />}
            {p === 'profile' && <ProfilePage />}
          </div>
        ))}

        {renderTransientPage()}

        {showNav && <BottomNav />}
        <Toast />
        <ExitConfirmDialog />
        <PrivacyConsentModal />
        <UpdateDialog />
      </div>
    </div>
  )
}

export default function App() {
  return (
    <AppProvider>
      <AppInner />
    </AppProvider>
  )
}
